/**
 * Email Template Service
 *
 * Provides CRUD access to email templates via the backend API.
 * All functions call /api/email-templates endpoints.
 */

import { getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

export interface ApiEmailTemplate {
  id: number;
  name: string;
  description: string | null;
  subject: string;
  body: string;
  category: string;
  variables: string | null;
  thumbnail: string | null;
  isDefault: boolean;
  attachInvoiceCopies: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  organisationId: string | null;
  companyId: number | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchEmailTemplates(): Promise<ApiEmailTemplate[]> {
  const data = await apiFetch<ApiEmailTemplate[] | Record<string, unknown>>(`${getApiBaseUrl()}/email-templates`);
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && '$values' in data && Array.isArray(data.$values)) {
    return data.$values as ApiEmailTemplate[];
  }
  return [];
}

export async function fetchEmailTemplateById(id: number): Promise<ApiEmailTemplate> {
  return apiFetch<ApiEmailTemplate>(`${getApiBaseUrl()}/email-templates/${id}`);
}

export async function createEmailTemplate(
  template: Omit<ApiEmailTemplate, 'id' | 'createdBy' | 'updatedBy' | 'organisationId' | 'companyId' | 'createdAt' | 'updatedAt'>,
): Promise<ApiEmailTemplate> {
  return apiFetch<ApiEmailTemplate>(`${getApiBaseUrl()}/email-templates`, {
    method: 'POST',
    body: JSON.stringify(template),
  });
}

export async function updateEmailTemplate(
  id: number,
  template: ApiEmailTemplate,
): Promise<void> {
  await apiFetch<string>(`${getApiBaseUrl()}/email-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(template),
  });
}

export async function deleteEmailTemplate(id: number): Promise<void> {
  await apiFetch<string>(`${getApiBaseUrl()}/email-templates/${id}`, {
    method: 'DELETE',
  });
}

export async function setEmailTemplateDefault(id: number): Promise<void> {
  await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/email-templates/${id}/set-default`, {
    method: 'POST',
  });
}
